// Fase 17.3, Plano 06 — estado do editor de etapas do roteiro (D-14, D-18, D-22): funções puras, sem React.
// O editor guarda texto (tempo em minutos, quantidade em pt-BR) e converte para o contrato só ao salvar.
import { interpretarNumeroPtBr, mensagemNumeroAmbiguo, type EtapaRoteiroDto, type EtapaRoteiroEntradaDto, type ServicoComCelulasDto } from '@pieltec/contracts';

export interface InsumoEdicao {
  readonly insumoItemId: string;
  readonly quantidade: string;
}

export interface EtapaEdicao {
  readonly chave: string;
  readonly servicoId: string;
  /** Código da célula padrão (é o `celulaId` do contrato). */
  readonly celulaId: string;
  /** Tempo padrão em minutos, texto pt-BR; vazio = sem tempo padrão (D-22). */
  readonly tempoMinutos: string;
  readonly usaTintaDaPeca: boolean;
  readonly insumos: readonly InsumoEdicao[];
}

/** Serviços cuja etapa consome a tinta da peça por padrão (mesma regra do importador do CargaMáquina). */
const SERVICOS_COM_TINTA: ReadonlySet<string> = new Set(['06', '07', '08']);
const LIMITE_MINUTOS = 1440;
const LIMITE_QUANTIDADE = 100000;

let contadorDeChaves = 0;

export function novaChave(): string {
  contadorDeChaves += 1;
  return `etapa-${contadorDeChaves}`;
}

/** Célula padrão de um serviço: a de menor código entre as ativas ('' quando não há nenhuma). */
export function celulaPadraoDoServico(servico: ServicoComCelulasDto | undefined): string {
  const ativas = (servico?.celulas ?? []).filter((celula) => celula.ativo);
  return [...ativas].sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR'))[0]?.codigo ?? '';
}

export function novaEtapa(servico: ServicoComCelulasDto): EtapaEdicao {
  return {
    chave: novaChave(),
    servicoId: servico.id,
    celulaId: celulaPadraoDoServico(servico),
    tempoMinutos: '',
    usaTintaDaPeca: SERVICOS_COM_TINTA.has(servico.codigo),
    insumos: [],
  };
}

export function trocarServico(etapa: EtapaEdicao, servico: ServicoComCelulasDto): EtapaEdicao {
  return { ...etapa, servicoId: servico.id, celulaId: celulaPadraoDoServico(servico) };
}

function formatarMinutos(segundos: number): string {
  const minutos = segundos / 60;
  return Number.isInteger(minutos) ? String(minutos) : minutos.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function formatarQuantidade(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 6, useGrouping: false });
}

/** Etapas de uma versão salva -> estado do editor. Linhas de tinta calculadas pela área (AREA_TINTA) não são editáveis e ficam de fora. */
export function etapasDaVersao(etapas: readonly EtapaRoteiroDto[], servicos: readonly ServicoComCelulasDto[]): EtapaEdicao[] {
  return [...etapas].sort((a, b) => a.etapaOrdem - b.etapaOrdem).map((etapa) => {
    const servico = servicos.find((s) => s.id === etapa.servicoId)
      ?? servicos.find((s) => s.codigo === etapa.codigoServico)
      ?? servicos.find((s) => s.celulas.some((celula) => celula.codigo === etapa.celulaId));
    return {
      chave: novaChave(),
      servicoId: servico?.id ?? etapa.servicoId ?? '',
      celulaId: etapa.celulaId,
      tempoMinutos: etapa.tempoPadraoSegundos ? formatarMinutos(etapa.tempoPadraoSegundos) : '',
      usaTintaDaPeca: etapa.usaTintaDaPeca === true,
      insumos: (etapa.insumosPrevistos ?? [])
        .filter((insumo) => insumo.origem !== 'AREA_TINTA')
        .map((insumo) => ({ insumoItemId: insumo.insumoItemId, quantidade: formatarQuantidade(insumo.quantidadePrevista) })),
    };
  });
}

export function moverEtapa<T>(lista: readonly T[], indice: number, delta: number): T[] {
  const destino = indice + delta;
  if (indice < 0 || indice >= lista.length || destino < 0 || destino >= lista.length) return [...lista];
  const copia = [...lista];
  const [movida] = copia.splice(indice, 1) as [T];
  copia.splice(destino, 0, movida);
  return copia;
}

export function removerEtapa<T>(lista: readonly T[], indice: number): T[] {
  return lista.filter((_, posicao) => posicao !== indice);
}

type LeituraPositiva = { readonly tipo: 'ok'; readonly valor: number } | { readonly tipo: 'vazio' } | { readonly tipo: 'invalido'; readonly mensagem?: string };

function lerPositivo(texto: string, maximo: number): LeituraPositiva {
  const lido = interpretarNumeroPtBr(texto);
  if (lido.tipo === 'vazio') return { tipo: 'vazio' };
  if (lido.tipo === 'ambiguo') return { tipo: 'invalido', mensagem: mensagemNumeroAmbiguo(texto, lido) };
  if (lido.tipo !== 'ok' || !(lido.valor > 0) || lido.valor > maximo) return { tipo: 'invalido' };
  return { tipo: 'ok', valor: lido.valor };
}

/** Mensagens em pt-BR para a lista de etapas; vazio = pode salvar. */
export function validarEtapas(lista: readonly EtapaEdicao[]): string[] {
  if (lista.length === 0) return ['Inclua pelo menos uma etapa.'];
  const mensagens: string[] = [];
  lista.forEach((etapa, indice) => {
    const ordem = indice + 1;
    if (!etapa.servicoId) mensagens.push(`Etapa ${ordem}: escolha o serviço.`);
    if (!etapa.celulaId) mensagens.push(`Etapa ${ordem}: escolha a célula padrão.`);
    if (etapa.tempoMinutos.trim() !== '') {
      const tempo = lerPositivo(etapa.tempoMinutos, LIMITE_MINUTOS);
      if (tempo.tipo !== 'ok') mensagens.push(`Etapa ${ordem}: tempo padrão deve ser um número de minutos maior que zero.`);
    }
    // Cada insumo inválido cita o próprio número: duas linhas com o mesmo erro viram duas mensagens distintas (chaves únicas na lista).
    etapa.insumos.forEach((insumo, posicao) => {
      const onde = `Etapa ${ordem}, insumo ${posicao + 1}`;
      if (insumo.insumoItemId.trim() === '') {
        mensagens.push(`${onde}: informe o código do insumo.`);
        return;
      }
      const quantidade = lerPositivo(insumo.quantidade, LIMITE_QUANTIDADE);
      if (quantidade.tipo === 'invalido' && quantidade.mensagem) mensagens.push(`${onde}: ${quantidade.mensagem}`);
      else if (quantidade.tipo !== 'ok') mensagens.push(`${onde}: quantidade do insumo deve ser maior que zero.`);
    });
  });
  return mensagens;
}

/** Estado do editor -> corpo da API. Minutos viram segundos inteiros; tempo vazio é omitido (D-22). */
export function etapasParaSalvar(lista: readonly EtapaEdicao[]): EtapaRoteiroEntradaDto[] {
  return lista.map((etapa) => {
    const tempo = lerPositivo(etapa.tempoMinutos, LIMITE_MINUTOS);
    return {
      celulaId: etapa.celulaId,
      ...(etapa.servicoId ? { servicoId: etapa.servicoId } : {}),
      ...(tempo.tipo === 'ok' ? { tempoPadraoSegundos: Math.max(1, Math.round(tempo.valor * 60)) } : {}),
      usaTintaDaPeca: etapa.usaTintaDaPeca,
      insumosPrevistos: etapa.insumos.map((insumo) => {
        const quantidade = lerPositivo(insumo.quantidade, LIMITE_QUANTIDADE);
        return { insumoItemId: insumo.insumoItemId.trim(), quantidadePrevista: quantidade.tipo === 'ok' ? quantidade.valor : 0 };
      }),
    };
  });
}
